import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Stack,
  Autocomplete,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import {
  Commission,
  CommissionTier,
  CreateCommissionData,
  UpdateCommissionData,
  ZONE_TYPES,
  PROVIDERS,
  useProviders,
  useServiceTypes,
} from '../../hooks/admin/useAdminCommissions';
import { ApiAutocomplete } from '../common';
import ZoneBuilder, { ZoneEntry } from './commission/ZoneBuilder';
import TierEditor from './commission/TierEditor';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ledgerInputSx,
  ledgerChipSx,
  ledgerSwitchSx,
  sectionEyebrowSx,
  sectionTitleSx,
  fieldLabelSx,
} from '../../styles/ledger';

interface User {
  id: number;
  name: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar?: string;
}

interface CommissionFormProps {
  initialData?: Commission;
  onSubmit: (data: CreateCommissionData | UpdateCommissionData) => Promise<void>;
  isSubmitting?: boolean;
  isEditMode?: boolean;
}

interface FormData {
  user_ids: number[] | null;
  selectedUsers: User[] | null;
  providers: string[] | null;
  service_types: string[] | null;
  origin_zones: ZoneEntry[];
  destination_zones: ZoneEntry[];
  conditions: {
    package_type: string;
    weight: { min: number | undefined; max: number | undefined };
  };
  commission_tiers: CommissionTier[];
  priority: number;
  is_active: boolean;
}

/* ─────────────────────────────────────────────
   SECTION — editorial numbered block
   ───────────────────────────────────────────── */
const Section: React.FC<{
  number: string;
  eyebrow: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}> = ({ number, eyebrow, title, description, children }) => (
  <Box
    sx={{
      borderTop: `1px solid ${INK.hairline}`,
      pt: 4,
      pb: 1,
    }}
  >
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 6 }}>
      {/* Left rail: number + eyebrow + title */}
      <Box sx={{ flex: { md: '0 0 220px' }, minWidth: { md: 220 } }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 11,
              color: INK.accent,
              fontWeight: 600,
              letterSpacing: '0.04em',
            }}
          >
            § {number}
          </Typography>
          <Typography sx={sectionEyebrowSx}>{eyebrow}</Typography>
        </Stack>
        <Typography sx={{ ...sectionTitleSx, fontSize: 22, mb: description ? 1 : 0 }}>
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontFamily: FONTS.sans,
              fontSize: 12.5,
              color: INK.muted,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      {/* Right rail: form content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  </Box>
);

const CommissionForm: React.FC<CommissionFormProps> = ({
  initialData,
  onSubmit,
  isSubmitting = false,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<FormData>({
    user_ids: null,
    selectedUsers: null,
    providers: null,
    service_types: null,
    origin_zones: [],
    destination_zones: [],
    conditions: { package_type: '', weight: { min: undefined, max: undefined } },
    commission_tiers: [],
    priority: 1,
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: providerOptions = PROVIDERS } = useProviders();
  const { data: serviceTypes = {} } = useServiceTypes(formData.providers);
  const zoneTypeLabels = useMemo<Record<string, string>>(
    () => Object.fromEntries(ZONE_TYPES.map((z) => [z.value, z.label])),
    []
  );

  const serviceOptions = useMemo(
    () =>
      Object.entries(serviceTypes).flatMap(([category, services]) =>
        Object.entries(services as Record<string, string>).map(([code, name]) => ({
          value: code,
          label: name,
          category,
        }))
      ),
    [serviceTypes]
  );

  const mapZonesWithLabels = useCallback(
    (zones?: Array<{ type: string; id: number }> | null, details?: Array<{ type: string; id: number; data: any }> | null) => {
      const detailMap = new Map<string, any>();
      details?.forEach((item) => detailMap.set(`${item.type}-${item.id}`, item.data));
      return (zones || []).map((zone) => {
        const detail = detailMap.get(`${zone.type}-${zone.id}`);
        const label = detail?.name || `${zoneTypeLabels[zone.type] || zone.type} #${zone.id}`;
        return { ...zone, data: { label, ...detail } };
      });
    },
    [zoneTypeLabels]
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        user_ids: initialData.user_ids || null,
        selectedUsers: initialData.users || null,
        providers: initialData.providers || null,
        service_types: initialData.service_types || null,
        origin_zones: mapZonesWithLabels(initialData.origin_zones, initialData.origin_zone_details),
        destination_zones: mapZonesWithLabels(initialData.destination_zones, initialData.destination_zone_details),
        conditions: {
          package_type: initialData.conditions?.package_type || '',
          weight: {
            min: initialData.conditions?.weight?.min || undefined,
            max: initialData.conditions?.weight?.max || undefined,
          },
        },
        commission_tiers:
          Array.isArray(initialData.commission_tiers) && initialData.commission_tiers.length > 0
            ? initialData.commission_tiers
            : [],
        priority: initialData.priority,
        is_active: initialData.is_active,
      });
    }
  }, [initialData, mapZonesWithLabels]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.commission_tiers || formData.commission_tiers.length === 0) {
      newErrors.commission_tiers = 'Add at least one commission tier';
    }
    if (formData.priority < 1) newErrors.priority = 'Priority must be at least 1';
    if (formData.conditions.weight.min !== undefined && formData.conditions.weight.max !== undefined) {
      if (formData.conditions.weight.min >= formData.conditions.weight.max) {
        newErrors.weight_max = 'Maximum weight must be greater than minimum weight';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    try {
      const submitData: CreateCommissionData | UpdateCommissionData = {
        user_ids: formData.user_ids && formData.user_ids.length > 0 ? formData.user_ids : null,
        providers: formData.providers && formData.providers.length > 0 ? formData.providers : null,
        service_types: formData.service_types && formData.service_types.length > 0 ? formData.service_types : null,
        origin_zones:
          formData.origin_zones.length > 0 ? formData.origin_zones.map(({ type, id }) => ({ type, id })) : null,
        destination_zones:
          formData.destination_zones.length > 0
            ? formData.destination_zones.map(({ type, id }) => ({ type, id }))
            : null,
        conditions: {
          package_type: formData.conditions.package_type || undefined,
          weight:
            formData.conditions.weight.min !== undefined || formData.conditions.weight.max !== undefined
              ? { min: formData.conditions.weight.min, max: formData.conditions.weight.max }
              : undefined,
        },
        commission_tiers: formData.commission_tiers.length > 0 ? formData.commission_tiers : undefined,
        priority: formData.priority,
        is_active: formData.is_active,
      };
      await onSubmit(submitData);
    } catch (err) {
      console.error('Form submission error:', err);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        fontFamily: FONTS.sans,
        bgcolor: INK.bg,
        mx: -3,
        my: -2,
        px: { xs: 3, md: 5 },
        py: { xs: 3, md: 4 },
      }}
    >
      {/* ─── 01 ASSIGNMENT ─── */}
      <Box sx={{ pb: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 6 }} sx={{ pt: 1, pb: 4 }}>
          <Box sx={{ flex: { md: '0 0 220px' }, minWidth: { md: 220 } }}>
            <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.accent, fontWeight: 600, letterSpacing: '0.04em' }}>§ 01</Typography>
              <Typography sx={sectionEyebrowSx}>Assignment</Typography>
            </Stack>
            <Typography sx={{ ...sectionTitleSx, fontSize: 22, mb: 1 }}>
              Who does this <Box component="em" sx={{ color: INK.accent, fontStyle: 'italic' }}>apply</Box> to?
            </Typography>
            <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12.5, color: INK.muted, lineHeight: 1.5 }}>
              Leave empty for a general commission that catches every user without a more specific override.
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ApiAutocomplete<User>
              multiple
              endpoint="admin/users"
              searchParamName="search"
              labelKey={(u) => u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
              valueKey="id"
              secondaryLabelKey="email"
              value={formData.selectedUsers || []}
              onChange={(users) => {
                const selectedUsers = users as User[] | null;
                setFormData((prev) => ({
                  ...prev,
                  selectedUsers,
                  user_ids: selectedUsers && selectedUsers.length > 0 ? selectedUsers.map((u) => u.id) : null,
                }));
              }}
              label="Assigned users"
              placeholder="Search by name or email…"
              minSearchLength={2}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id} sx={{ fontFamily: FONTS.sans }}>
                  <Box display="flex" alignItems="center" width="100%">
                    {option.avatar && (
                      <Box
                        component="img"
                        src={option.avatar}
                        alt={option.first_name + ' ' + option.last_name}
                        sx={{ width: 28, height: 28, borderRadius: '50%', mr: 1.5 }}
                      />
                    )}
                    <Box>
                      <Typography sx={{ fontSize: 13.5, color: INK.text, fontWeight: 500 }}>
                        {option.first_name} {option.last_name}
                      </Typography>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              textFieldProps={{
                helperText: 'Empty → general commission for all users',
                size: 'medium',
                sx: ledgerInputSx,
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* ─── 02 CHANNEL ─── */}
      <Section
        number="02"
        eyebrow="Channel"
        title="Provider & service scope"
        description="Limit which providers and service types this commission applies to. Both empty means it covers every channel."
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={fieldLabelSx}>Providers</Typography>
            <Autocomplete
              multiple
              size="small"
              options={providerOptions}
              getOptionLabel={(o) => o.label}
              value={providerOptions.filter((p) => formData.providers?.includes(p.value)) || []}
              onChange={(_, newValue) => {
                const providerValues = newValue.length > 0 ? newValue.map((v) => v.value) : null;
                handleChange('providers', providerValues);
                handleChange('service_types', null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All providers"
                  helperText="Empty → matches all providers"
                  sx={ledgerInputSx}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.label} {...getTagProps({ index })} size="small" sx={ledgerChipSx} />
                ))
              }
            />
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Service types</Typography>
            <Autocomplete
              multiple
              size="small"
              disabled={!formData.providers || formData.providers.length === 0}
              options={serviceOptions}
              groupBy={(o) => o.category.toUpperCase()}
              getOptionLabel={(o) => o.label}
              value={serviceOptions.filter((st) => formData.service_types?.includes(st.value))}
              onChange={(_, newValue) => {
                const v = newValue.length > 0 ? newValue.map((x) => x.value) : null;
                handleChange('service_types', v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={!formData.providers || formData.providers.length === 0 ? 'Pick a provider first' : 'All services'}
                  helperText="Empty → matches all service types"
                  sx={ledgerInputSx}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.label} {...getTagProps({ index })} size="small" sx={ledgerChipSx} />
                ))
              }
            />
          </Box>
        </Stack>
      </Section>

      {/* ─── 03 CORRIDOR ─── */}
      <Section
        number="03"
        eyebrow="Corridor"
        title="Origin & destination"
        description="Define the shipping route this commission applies to. Skip both to cover every corridor."
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ZoneBuilder
              direction="origin"
              zones={formData.origin_zones}
              onChange={(zones) => handleChange('origin_zones', zones)}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 32,
              color: INK.faint,
              transform: { xs: 'rotate(90deg)', md: 'none' },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ZoneBuilder
              direction="destination"
              zones={formData.destination_zones}
              onChange={(zones) => handleChange('destination_zones', zones)}
            />
          </Box>
        </Stack>
      </Section>

      {/* ─── 04 SETTINGS ─── */}
      <Section
        number="04"
        eyebrow="Settings"
        title="Priority & activation"
        description="Lower priority numbers fire first. Toggle off to pause without deleting."
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-end' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Priority</Typography>
            <TextField
              type="number"
              value={formData.priority}
              onChange={(e) => handleChange('priority', parseInt(e.target.value) || 1)}
              fullWidth
              size="small"
              error={!!errors.priority}
              helperText={errors.priority || 'Lower number = applied first'}
              InputProps={{ inputProps: { min: 1 } }}
              sx={ledgerInputSx}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Status</Typography>
            <Box
              sx={{
                border: `1px solid ${INK.hairline}`,
                borderRadius: 0.5,
                px: 2,
                py: 1,
                bgcolor: INK.surface,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: formData.is_active ? INK.active : INK.inactive,
                    boxShadow: formData.is_active ? `0 0 0 3px ${INK.accentSoft}` : 'none',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: FONTS.mono,
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: formData.is_active ? INK.active : INK.inactive,
                    fontWeight: 500,
                  }}
                >
                  {formData.is_active ? 'Active' : 'Paused'}
                </Typography>
              </Stack>
              <Switch
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                size="small"
                sx={ledgerSwitchSx}
              />
            </Box>
          </Box>
        </Stack>
      </Section>

      {/* ─── 05 TIERS ─── */}
      <Section
        number="05"
        eyebrow="Tiers"
        title="Weight-based rates"
        description="Define different commission rates per weight band. At least one tier is required."
      >
        <TierEditor
          tiers={formData.commission_tiers}
          onChange={(tiers) => handleChange('commission_tiers', tiers)}
          error={errors.commission_tiers}
        />
      </Section>

      {/* ─── Submit row ─── */}
      <Box sx={{ borderTop: `1px solid ${INK.hairline}`, mt: 4, pt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          {isEditMode ? 'Edit mode' : 'Draft'} · 5 sections
        </Typography>
        <Button
          type="submit"
          disabled={isSubmitting}
          sx={{ ...primaryBtnSx, px: 3, py: 1.25 }}
          startIcon={isSubmitting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          {isSubmitting ? 'Saving…' : isEditMode ? 'Update commission' : 'Create commission'}
        </Button>
      </Box>
    </Box>
  );
};

export default CommissionForm;
